import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    HostListener,
    inject,
    Input,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';

import { QWYK_TABLE } from './table.component';

@Component({
    standalone: true,
    imports: [],
    selector: 'qwyk-sort-icon',
    template: `
        <i class="p-sortable-column-icon pi"
           aria-hidden="true"
           [class.pi-sort]="!isActive"
           [class.pi-sort-amount-up-alt]="isAsc"
           [class.pi-sort-amount-down]="isDesc">
        </i>
    `,
    // Inline host styles give the icon a generous click target + visual feedback.
    // Exposed as a keyboard-operable button with an accessible name and sort state.
    host: {
        '[style.display]': '"inline-flex"',
        '[style.alignItems]': '"center"',
        '[style.padding]': '"4px 6px"',
        '[style.cursor]': '"pointer"',
        '[style.borderRadius]': '"3px"',
        role: 'button',
        '[attr.tabindex]': '0',
        '[attr.aria-label]': 'ariaLabel',
        '[attr.aria-pressed]': 'isActive',
        '[attr.title]': 'ariaLabel',
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class QwykSortIconComponent implements OnInit {
    @Input() field: string;
    /** Accessible name of the column this control sorts (falls back to `field`) */
    @Input() label: string;

    private readonly _table = inject(QWYK_TABLE);
    private readonly _cdr = inject(ChangeDetectorRef);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _translate = inject(TranslateService);

    ngOnInit(): void {
        merge(this._table._sortState$, this._translate.onLangChange)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => this._cdr.markForCheck());
    }

    @HostListener('click', ['$event'])
    onClick(e: MouseEvent): void {
        e.stopPropagation(); // don't bubble to th or trigger reorder drag
        this._sort();
    }

    @HostListener('keydown.enter', ['$event'])
    @HostListener('keydown.space', ['$event'])
    onKeydown(e: KeyboardEvent): void {
        e.preventDefault(); // stop Space from scrolling the page
        e.stopPropagation();
        this._sort();
    }

    private _sort(): void {
        if (this.field) {
            this._table.sort(this.field);
        }
    }

    get ariaLabel(): string {
        const params = { value: this.label || this.field };
        if (this.isAsc) {
            return this._translate.instant('common.sort-by-field-ascending', params);
        }
        if (this.isDesc) {
            return this._translate.instant('common.sort-by-field-descending', params);
        }
        return this._translate.instant('common.sort-by-field', params);
    }

    get isActive(): boolean {
        return this._table._sortField === this.field && this._table._sortOrder !== 0;
    }

    get isAsc(): boolean {
        return this._table._sortField === this.field && this._table._sortOrder === 1;
    }

    get isDesc(): boolean {
        return this._table._sortField === this.field && this._table._sortOrder === -1;
    }
}
